/* eslint-disable roros/no-raw-html-in-routes */
// reason: pre-existing boilerplate pricing page; raw button elements intentional for static page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../components/ui/Container/Container';
import { Button } from '../components/ui/Button/Button';
import { Badge } from '../components/ui/Badge/Badge';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';
import { t } from '@/lib/i18n';

export function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const plans = [
    {
      name: t('Free'),
      description: t('Perfect for getting started'),
      price: { monthly: 0, yearly: 0 },
      features: [
        t('Up to 5 projects'),
        t('Basic templates'),
        t('Standard export formats'),
        t('Community support'),
        t('Basic AI features'),
        t('1GB storage')
      ],
      limitations: [
        t('Limited to 50 assets/month'),
        t('Platform branding'),
        t('No team collaboration')
      ],
      popular: false,
      cta: t('Get Started Free'),
      ctaLink: '/auth'
    },
    {
      name: t('Pro'),
      description: t('For growing creators and small teams'),
      price: { monthly: 29, yearly: 24 },
      features: [
        t('Unlimited projects'),
        t('Premium templates'),
        t('All export formats'),
        t('Priority email support'),
        t('Advanced AI features'),
        t('10GB storage'),
        t('Custom branding'),
        t('Team collaboration (up to 5 members)'),
        t('Advanced analytics')
      ],
      limitations: [],
      popular: true,
      cta: t('Start Pro Trial'),
      ctaLink: '/auth'
    },
    {
      name: t('Enterprise'),
      description: t('For large teams and organizations'),
      price: { monthly: 99, yearly: 79 },
      features: [
        t('Everything in Pro'),
        t('Unlimited team members'),
        t('100GB storage'),
        t('Dedicated account manager'),
        t('Phone & chat support'),
        t('Custom integrations'),
        t('Advanced security features'),
        t('SLA guarantee'),
        t('Custom training')
      ],
      limitations: [],
      popular: false,
      cta: t('Contact Sales'),
      ctaLink: '/contact'
    }
  ];

  const savings = billingCycle === 'yearly' ? t('Save 20%') : null;

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-card py-16">
        <Container size="lg">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Choose the perfect plan for your creative needs. All plans include our core features with no hidden fees.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-muted rounded-lg p-1 mb-8">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly
                {savings && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {savings}
                  </Badge>
                )}
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* Pricing Cards */}
      <section className="py-16">
        <Container size="lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`relative bg-card rounded-xl shadow-sm border-2 transition-all duration-300 hover:shadow-lg ${
                  plan.popular ? 'border-primary-500 scale-105' : 'border-border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge variant="default" className="px-3 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
                    <p className="text-muted-foreground mb-4">{plan.description}</p>

                    <div className="mb-4">
                      <span className="text-4xl font-bold text-foreground">
                        ${plan.price[billingCycle]}
                      </span>
                      {plan.price[billingCycle] > 0 && (
                        <span className="text-muted-foreground ml-1">
                          /{billingCycle === 'monthly' ? 'month' : 'month'}
                        </span>
                      )}
                      {billingCycle === 'yearly' && plan.price.yearly > 0 && (
                        <div className="text-sm text-muted-foreground mt-1">
                          Billed ${plan.price.monthly * 12}/year
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">What's included:</h4>
                      <ul className="space-y-2">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <svg className="w-5 h-5 text-success-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {plan.limitations.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3">Limitations:</h4>
                        <ul className="space-y-2">
                          {plan.limitations.map((limitation, limitIndex) => (
                            <li key={limitIndex} className="flex items-start">
                              <svg className="w-5 h-5 text-muted-foreground mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm text-muted-foreground">{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <Link to={plan.ctaLink} className="block">
                    <Button
                      variant={plan.popular ? 'primary' : 'outline'}
                      size="lg"
                      fullWidth
                      className={plan.popular ? 'shadow-lg' : ''}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-card">
        <Container size="lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <p className="text-muted-foreground">Everything you need to know about our pricing</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Can I change plans anytime?</h3>
              <p className="text-muted-foreground">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately,
                and we'll prorate any charges or credits accordingly.
              </p>
            </div>

            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Is there a free trial?</h3>
              <p className="text-muted-foreground">
                Our Free plan is available forever with no trial period needed. For Pro plans,
                we offer a 14-day free trial so you can test all features risk-free.
              </p>
            </div>

            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">What payment methods do you accept?</h3>
              <p className="text-muted-foreground">
                We accept all major credit cards, PayPal, and bank transfers for annual plans.
                All payments are processed securely through Stripe.
              </p>
            </div>

            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Can I cancel anytime?</h3>
              <p className="text-muted-foreground">
                Yes, you can cancel your subscription at any time. You'll continue to have access
                to your plan until the end of your current billing period.
              </p>
            </div>

            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Do you offer refunds?</h3>
              <p className="text-muted-foreground">
                We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied,
                contact our support team for a full refund.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <Container size="lg">
          <div className="text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of creators who trust our platform for their needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth">
                <Button variant="secondary" size="lg" className="bg-white text-primary-700 hover:bg-white/90">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" size="lg" className="text-white hover:bg-white/10">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <PublicFooter />
    </div>
  );
}
